import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const APPROVER_TYPES = ["USER", "ROLE", "REPORTING_MANAGER", "BUDGET_OWNER"];

const EMPTY_STEP = (order = 1) => ({
  id: null,
  stepOrder: order,
  name: "",
  approverType: "USER",
  approverValue: "",
  isParallel: false,
  required: true,
  slaHours: 48,
});

export default function ApprovalChainEditor({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = mode === "new";

  const [chain, setChain] = useState({
    name: "",
    description: "",
    appliesTo: "OFFER",
    isDefault: false,
    triggerRules: "{}",
  });
  const [steps, setSteps] = useState([EMPTY_STEP(1)]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    axiosInstance.get(`${baseUrl}/job-posting/approval-chains/${id}`)
      .then((r) => {
        if (cancelled) return;
        setChain({
          ...r.data?.chain,
          triggerRules: r.data?.chain?.triggerRules || "{}",
        });
        const ss = Array.isArray(r.data?.steps) ? r.data.steps : [];
        setSteps(ss.length > 0 ? ss : [EMPTY_STEP(1)]);
      })
      .catch((e) => setError(e?.message || "Failed to load"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isNew]);

  const updateStep = (idx, patch) => {
    setSteps((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const addStep = () => setSteps((prev) => [...prev, EMPTY_STEP(prev.length + 1)]);
  const removeStep = (idx) => setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepOrder: i + 1 })));
  const move = (idx, delta) => {
    setSteps((prev) => {
      const next = idx + delta;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(next, 0, item);
      return copy.map((s, i) => ({ ...s, stepOrder: i + 1 }));
    });
  };

  const validation = (() => {
    if (!chain.name.trim()) return "Chain name is required";
    if (steps.length === 0) return "Add at least one approval step";
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].name.trim()) return `Step ${i + 1} name is required`;
      if (!steps[i].approverValue.trim()) return `Step ${i + 1} approver value is required`;
    }
    try { JSON.parse(chain.triggerRules || "{}"); }
    catch { return "Trigger rules must be valid JSON"; }
    return null;
  })();

  const save = async () => {
    if (validation) { setError(validation); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { chain, steps };
      let saved;
      if (isNew) {
        const r = await axiosInstance.post(`${baseUrl}/job-posting/approval-chains`, payload);
        saved = r.data?.chain;
      } else {
        await axiosInstance.put(`${baseUrl}/job-posting/approval-chains/${id}`, payload);
      }
      setToast("Saved");
      setTimeout(() => setToast(null), 1500);
      if (isNew && saved?.id) nav(`/admin/approval-chains/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e?.data?.error || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-vf-muted">Loading…</div>;

  return (
    <div className="page-enter max-w-4xl">
      <div className="flex items-start justify-between mb-5">
        <div>
          <button onClick={() => nav("/admin/approval-chains")} className="text-xs text-vf-muted hover:underline mb-1">← Back</button>
          <h1 className="text-2xl font-semibold text-vf-base">{isNew ? "New approval chain" : `Edit · ${chain.name || "Untitled"}`}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving || !!validation}
                  className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700 mb-4">{error}</div>}
      {toast && <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-700 mb-4">{toast}</div>}

      <Section title="Chain">
        <Field label="Name" required>
          <input type="text" value={chain.name} onChange={(e) => setChain({ ...chain, name: e.target.value })}
                 placeholder="e.g. Engineering Offers"
                 className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
        </Field>
        <Field label="Description">
          <textarea value={chain.description || ""} onChange={(e) => setChain({ ...chain, description: e.target.value })}
                    rows={2} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
        </Field>
        <Row>
          <Field label="Applies to">
            <select value={chain.appliesTo} onChange={(e) => setChain({ ...chain, appliesTo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              <option value="OFFER">OFFER</option>
            </select>
          </Field>
          <Field label="Default for this type">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!chain.isDefault}
                     onChange={(e) => setChain({ ...chain, isDefault: e.target.checked })} />
              Set as default
            </label>
          </Field>
        </Row>
        <Field label="Trigger rules (JSON) — empty {} matches every offer">
          <textarea value={chain.triggerRules} onChange={(e) => setChain({ ...chain, triggerRules: e.target.value })}
                    rows={3}
                    placeholder='{"salaryMin": 2000000}'
                    className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono" />
          <div className="text-[10px] text-vf-muted mt-1">
            Keys supported: <code>salaryMin</code>, <code>salaryMax</code>, <code>jobLevels</code> (array, matched against department).
          </div>
        </Field>
      </Section>

      <Section title={`Steps (${steps.length})`}>
        <div className="space-y-3">
          {steps.map((s, i) => (
            <div key={i} className="border border-slate-200 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs font-semibold text-vf-base flex-1">Step {i + 1}</div>
                <button onClick={() => move(i, -1)} disabled={i === 0}
                        className="px-1.5 py-0.5 rounded border border-slate-300 text-[11px] disabled:opacity-30">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === steps.length - 1}
                        className="px-1.5 py-0.5 rounded border border-slate-300 text-[11px] disabled:opacity-30">↓</button>
                <button onClick={() => removeStep(i)}
                        className="text-red-600 hover:bg-red-50 rounded px-1.5 py-0.5 text-xs">✕</button>
              </div>
              <Row>
                <Field label="Step name" required>
                  <input type="text" value={s.name} onChange={(e) => updateStep(i, { name: e.target.value })}
                         placeholder="e.g. HR Head"
                         className="w-full px-2 py-1 border border-slate-300 rounded text-xs" />
                </Field>
                <Field label="Approver type">
                  <select value={s.approverType} onChange={(e) => updateStep(i, { approverType: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                    {APPROVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </Row>
              <Field label={approverValueLabel(s.approverType)} required>
                <input type="text" value={s.approverValue} onChange={(e) => updateStep(i, { approverValue: e.target.value })}
                       placeholder={approverValuePlaceholder(s.approverType)}
                       className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
              </Field>
              <div className="flex gap-3 items-center text-xs">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={s.required} onChange={(e) => updateStep(i, { required: e.target.checked })} />
                  Required
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title="Approves in parallel with adjacent parallel steps. All parallel steps in a wave must approve before progressing.">
                  <input type="checkbox" checked={s.isParallel} onChange={(e) => updateStep(i, { isParallel: e.target.checked })} />
                  Parallel
                </label>
                <div className="ml-auto flex items-center gap-1">
                  <label className="text-[11px] text-vf-muted">SLA hours</label>
                  <input type="number" min="1" value={s.slaHours}
                         onChange={(e) => updateStep(i, { slaHours: Number(e.target.value) })}
                         className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-xs" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addStep}
                className="w-full mt-3 px-3 py-1.5 rounded border border-dashed border-slate-300 text-xs text-vf-muted hover:bg-slate-50">
          + Add step
        </button>
      </Section>
    </div>
  );
}

const approverValueLabel = (type) => ({
  USER: "User ID",
  ROLE: "Role name (e.g. AMPLE_HIRE_ADMIN)",
  REPORTING_MANAGER: "Distance (e.g. N+1)",
  BUDGET_OWNER: "Department",
}[type] || "Value");
const approverValuePlaceholder = (type) => ({
  USER: "260",
  ROLE: "AMPLE_HIRE_ADMIN",
  REPORTING_MANAGER: "N+1",
  BUDGET_OWNER: "Engineering",
}[type] || "");

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs text-vf-muted mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
